import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const TierDistributionChart = ({ data, loading }) => {
  const { t } = useTranslation();

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload?.[0]?.payload;
    return (
      <div
        className="rounded-lg px-3 py-2 text-xs"
        style={{ backgroundColor: '#1f1f1f', border: '1px solid rgba(229,32,42,0.4)', fontFamily: 'Inter, sans-serif' }}
      >
        <p className="font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.9)' }}>T{d?.tier}</p>
        <p style={{ color: '#E5202A' }}>{d?.count} {d?.count > 1 ? t('admin.performance.creatorPlural', 'créateurs') : t('admin.performance.creatorSingular', 'créateur')}</p>
      </div>
    );
  };

  const chartData = useMemo(() => {
    if (!data?.length) return [];
    const tierCounts = {};
    for (let i = 1; i <= 10; i++) tierCounts[i] = 0;
    data?.forEach(r => {
      const tp = parseInt(r?.tier_p);
      if (!isNaN(tp) && tp >= 1 && tp <= 10) {
        tierCounts[tp]++;
      }
    });
    return Object.entries(tierCounts)?.map(([tier, count]) => ({ tier: parseInt(tier), count }));
  }, [data]);

  if (loading) {
    return (
      <div
        className="rounded-xl p-5 animate-pulse"
        style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', height: '240px' }}
      />
    );
  }

  if (!chartData?.length) {
    return (
      <div
        className="rounded-xl p-5 flex items-center justify-center"
        style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f', height: '200px' }}
      >
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performance.noDataAvailable', 'Aucune donnée disponible')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-5"
      style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}
    >
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#1f1f1f" />
          <XAxis
            dataKey="tier"
            tickFormatter={v => `T${v}`}
            tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: 'Inter, sans-serif' }}
            axisLine={{ stroke: '#1f1f1f' }}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={36}>
            {chartData?.map((entry, index) => {
              const opacity = 1 - (index / chartData?.length) * 0.5;
              return (
                <Cell
                  key={`cell-${index}`}
                  fill="#E5202A"
                  fillOpacity={opacity}
                />
              );
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TierDistributionChart;
